import {
  InterviewTranscriptionOutcome,
  resolveTranscriptionErrorOutcome,
} from './interviewTranscription';
import {
  InterviewObservabilityContext,
  interviewPerformanceNow,
  trackInterviewDuration,
  trackInterviewMetric,
} from './interviewObservability';

export type InterviewSttRecoveryPhase =
  | 'idle'
  | 'transcribing'
  | 'fast_retry_wait'
  | 'connectivity_wait'
  | 'exhausted'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type InterviewSttRecoverySnapshot = {
  phase: InterviewSttRecoveryPhase;
  recoveryId: string | null;
  requestAttempt: number;
  waitMs: number | null;
  errorClass: string | null;
  httpStatus: number | null;
  hasRetainedAudio: boolean;
};

export type InterviewSttRecoveryPolicy = {
  fastRetryDelaysMs: number[];
  extendedRetryDelaysMs: number[];
  extendedRetryMaxDelayMs: number;
  recoveryWindowMs: number;
  defaultRateLimitDelayMs: number;
};

export const INTERVIEW_STT_RECOVERY_POLICY: InterviewSttRecoveryPolicy = {
  // Initial request + these three waits = four deterministic fast attempts.
  fastRetryDelaysMs: [500, 1000, 2000],
  extendedRetryDelaysMs: [5000, 10000, 15000],
  extendedRetryMaxDelayMs: 15000,
  recoveryWindowMs: 120000,
  defaultRateLimitDelayMs: 5000,
};

type BrowserEventTarget = Pick<Window, 'addEventListener' | 'removeEventListener'>;

type RecoveryTask<T> = {
  recoveryId: string;
  blob: Blob | null;
  operation: (blob: Blob, requestAttempt: number, requestTimeoutMs: number) => Promise<T>;
  observability?: InterviewObservabilityContext;
  onStateChange?: (snapshot: InterviewSttRecoverySnapshot) => void;
  requestAttempt: number;
  recoveryStartedAt: number;
  windowStartedAt: number;
  connectivityWaitLogged: boolean;
  cancelled: boolean;
  promise: Promise<T> | null;
};

export class InterviewSttRecoveryFailure extends Error {
  outcome: InterviewTranscriptionOutcome;

  originalError: unknown;

  exhausted: boolean;

  constructor(outcome: InterviewTranscriptionOutcome, originalError: unknown, exhausted = false) {
    super(outcome.message || 'Transkripsi belum dapat diproses.');
    this.name = 'InterviewSttRecoveryFailure';
    this.outcome = outcome;
    this.originalError = originalError;
    this.exhausted = exhausted;
  }
}

export class InterviewSttRecoveryCancelledError extends Error {
  constructor() {
    super('STT recovery cancelled');
    this.name = 'InterviewSttRecoveryCancelledError';
  }
}

const readHttpStatus = (error: any) => Number(error?.response?.status) || null;

const normalizeDelay = (value: unknown, fallback = 1) => {
  if (value === null || value === undefined || value === '') return fallback;
  const delay = Number(value);
  return Number.isFinite(delay) && delay >= 0 ? Math.round(delay) : fallback;
};

const clonePolicy = (policy?: Partial<InterviewSttRecoveryPolicy>): InterviewSttRecoveryPolicy => ({
  ...INTERVIEW_STT_RECOVERY_POLICY,
  ...policy,
  fastRetryDelaysMs: Array.isArray(policy?.fastRetryDelaysMs)
    ? policy!.fastRetryDelaysMs.map((delay) => normalizeDelay(delay, 0))
    : [...INTERVIEW_STT_RECOVERY_POLICY.fastRetryDelaysMs],
  extendedRetryDelaysMs: Array.isArray(policy?.extendedRetryDelaysMs)
    ? policy!.extendedRetryDelaysMs.map((delay) => normalizeDelay(delay, 0))
    : [...INTERVIEW_STT_RECOVERY_POLICY.extendedRetryDelaysMs],
});

export class InterviewSttRecoveryCoordinator {
  private readonly policy: InterviewSttRecoveryPolicy;

  private readonly onlineTarget: BrowserEventTarget | null;

  private activeTask: RecoveryTask<any> | null = null;

  private waitTimer: ReturnType<typeof setTimeout> | null = null;

  private onlineListener: (() => void) | null = null;

  private waitResolve: ((source: 'timer' | 'online') => void) | null = null;

  private snapshot: InterviewSttRecoverySnapshot = {
    phase: 'idle',
    recoveryId: null,
    requestAttempt: 0,
    waitMs: null,
    errorClass: null,
    httpStatus: null,
    hasRetainedAudio: false,
  };

  constructor(options: {
    policy?: Partial<InterviewSttRecoveryPolicy>;
    onlineTarget?: BrowserEventTarget | null;
  } = {}) {
    this.policy = clonePolicy(options.policy);
    this.onlineTarget = options.onlineTarget === undefined
      ? (typeof window !== 'undefined' ? window : null)
      : options.onlineTarget;
  }

  getSnapshot() {
    return { ...this.snapshot };
  }

  hasRetainedAudio() {
    return Boolean(this.activeTask?.blob);
  }

  execute<T>({
    recoveryId,
    blob,
    operation,
    observability,
    onStateChange,
  }: {
    recoveryId: string;
    blob: Blob;
    operation: (blob: Blob, requestAttempt: number, requestTimeoutMs: number) => Promise<T>;
    observability?: InterviewObservabilityContext;
    onStateChange?: (snapshot: InterviewSttRecoverySnapshot) => void;
  }): Promise<T> {
    if (this.activeTask) {
      if (this.activeTask.recoveryId === recoveryId && this.activeTask.promise) {
        return this.activeTask.promise as Promise<T>;
      }
      return Promise.reject(new Error('An STT recovery chain is already active.'));
    }

    const now = interviewPerformanceNow();
    const task: RecoveryTask<T> = {
      recoveryId,
      blob,
      operation,
      observability,
      onStateChange,
      requestAttempt: 0,
      recoveryStartedAt: now,
      windowStartedAt: now,
      connectivityWaitLogged: false,
      cancelled: false,
      promise: null,
    };
    this.activeTask = task;
    task.promise = this.run(task);
    return task.promise;
  }

  cancel() {
    const task = this.activeTask;
    if (!task) return;
    task.cancelled = true;
    this.finishWait('timer');
  }

  private updateSnapshot(
    task: RecoveryTask<any>,
    phase: InterviewSttRecoveryPhase,
    details: Partial<InterviewSttRecoverySnapshot> = {},
  ) {
    this.snapshot = {
      phase,
      recoveryId: task.recoveryId,
      requestAttempt: task.requestAttempt,
      waitMs: null,
      errorClass: null,
      httpStatus: null,
      hasRetainedAudio: Boolean(task.blob),
      ...details,
    };
    task.onStateChange?.({ ...this.snapshot });
  }

  private metricPayload(task: RecoveryTask<any>, details: {
    waitMs?: number | null;
    errorClass?: string | null;
    httpStatus?: number | null;
    outcome?: string;
    stage?: string;
  } = {}) {
    return {
      ...task.observability,
      recoveryId: task.recoveryId,
      requestAttempt: task.requestAttempt,
      waitMs: details.waitMs,
      errorClass: details.errorClass,
      httpStatus: details.httpStatus,
      outcome: details.outcome,
      stage: details.stage,
    };
  }

  private async run<T>(task: RecoveryTask<T>): Promise<T> {
    let fastDelayIndex = 0;
    let extendedDelayIndex = 0;

    try {
      while (true) {
        if (task.cancelled) throw new InterviewSttRecoveryCancelledError();
        if (!task.blob) throw new InterviewSttRecoveryCancelledError();

        task.requestAttempt += 1;
        this.updateSnapshot(task, 'transcribing');
        if (task.requestAttempt > 1) {
          trackInterviewMetric('stt_retry_started', this.metricPayload(task, {
            stage: task.connectivityWaitLogged ? 'connectivity_recovery' : 'fast_retry',
          }));
        }

        const attemptStartedAt = interviewPerformanceNow();
        try {
          const remainingWindowMs = Math.max(
            1,
            Math.round(this.policy.recoveryWindowMs - (attemptStartedAt - task.windowStartedAt)),
          );
          const result = await task.operation(task.blob, task.requestAttempt, remainingWindowMs);
          if (task.cancelled) throw new InterviewSttRecoveryCancelledError();
          trackInterviewDuration('stt_attempt_ms', attemptStartedAt, this.metricPayload(task, {
            outcome: 'success',
          }));
          trackInterviewDuration('stt_recovery_completed', task.recoveryStartedAt, this.metricPayload(task, {
            outcome: task.requestAttempt === 1 ? 'first_attempt' : 'recovered',
          }));
          this.updateSnapshot(task, 'completed', { hasRetainedAudio: false });
          task.blob = null;
          this.activeTask = null;
          return result;
        } catch (error: any) {
          if (error instanceof InterviewSttRecoveryCancelledError || task.cancelled) {
            throw new InterviewSttRecoveryCancelledError();
          }

          const outcome = resolveTranscriptionErrorOutcome(error);
          const httpStatus = readHttpStatus(error);
          trackInterviewDuration('stt_attempt_ms', attemptStartedAt, this.metricPayload(task, {
            outcome: 'error',
            errorClass: outcome.recoveryClass,
            httpStatus,
          }));

          if (outcome.recoveryClass !== 'infrastructure' && outcome.recoveryClass !== 'rate_limit') {
            this.updateSnapshot(task, 'failed', {
              errorClass: outcome.recoveryClass,
              httpStatus,
              hasRetainedAudio: false,
            });
            task.blob = null;
            this.activeTask = null;
            throw new InterviewSttRecoveryFailure(outcome, error);
          }

          let waitMs: number;
          let allowOnlineWake = false;
          let nextPhase: InterviewSttRecoveryPhase;

          if (outcome.recoveryClass === 'rate_limit') {
            waitMs = normalizeDelay(outcome.retryAfterMs, this.policy.defaultRateLimitDelayMs);
            nextPhase = 'connectivity_wait';
          } else if (fastDelayIndex < this.policy.fastRetryDelaysMs.length) {
            waitMs = this.policy.fastRetryDelaysMs[fastDelayIndex];
            fastDelayIndex += 1;
            nextPhase = 'fast_retry_wait';
          } else {
            const configuredDelay = this.policy.extendedRetryDelaysMs[
              Math.min(extendedDelayIndex, Math.max(0, this.policy.extendedRetryDelaysMs.length - 1))
            ];
            waitMs = Math.min(
              normalizeDelay(configuredDelay, this.policy.extendedRetryMaxDelayMs),
              normalizeDelay(this.policy.extendedRetryMaxDelayMs, 15000),
            );
            extendedDelayIndex += 1;
            nextPhase = 'connectivity_wait';
            allowOnlineWake = true;
          }

          const elapsedInWindow = interviewPerformanceNow() - task.windowStartedAt;
          if (elapsedInWindow + waitMs > this.policy.recoveryWindowMs) {
            trackInterviewDuration('stt_recovery_exhausted', task.windowStartedAt, this.metricPayload(task, {
              outcome: 'window_exhausted',
              errorClass: outcome.recoveryClass,
              httpStatus,
            }));
            task.blob = null;
            this.updateSnapshot(task, 'exhausted', {
              errorClass: outcome.recoveryClass,
              httpStatus,
              hasRetainedAudio: false,
            });
            throw new InterviewSttRecoveryFailure(outcome, error, true);
          }

          if (nextPhase === 'connectivity_wait' && !task.connectivityWaitLogged) {
            task.connectivityWaitLogged = true;
            trackInterviewMetric('stt_connectivity_wait_started', this.metricPayload(task, {
              waitMs,
              errorClass: outcome.recoveryClass,
              httpStatus,
            }));
          }
          this.updateSnapshot(task, nextPhase, {
            waitMs,
            errorClass: outcome.recoveryClass,
            httpStatus,
          });
          trackInterviewMetric('stt_retry_scheduled', this.metricPayload(task, {
            waitMs,
            errorClass: outcome.recoveryClass,
            httpStatus,
            stage: outcome.recoveryClass === 'rate_limit' ? 'rate_limit' : nextPhase,
          }));

          const wakeSource = await this.wait(waitMs, allowOnlineWake);
          if (task.cancelled) throw new InterviewSttRecoveryCancelledError();
          if (wakeSource === 'online') {
            trackInterviewMetric('stt_online_retry_triggered', this.metricPayload(task, {
              waitMs: 0,
              errorClass: outcome.recoveryClass,
              httpStatus,
            }));
          }
        }
      }
    } catch (error) {
      if (error instanceof InterviewSttRecoveryCancelledError || task.cancelled) {
        this.updateSnapshot(task, 'cancelled', { hasRetainedAudio: false });
        task.blob = null;
        this.activeTask = null;
        throw new InterviewSttRecoveryCancelledError();
      }
      task.blob = null;
      this.activeTask = null;
      throw error;
    }
  }

  private wait(waitMs: number, allowOnlineWake: boolean) {
    return new Promise<'timer' | 'online'>((resolve) => {
      let settled = false;
      const finish = (source: 'timer' | 'online') => {
        if (settled) return;
        settled = true;
        if (this.waitTimer !== null) {
          clearTimeout(this.waitTimer);
          this.waitTimer = null;
        }
        if (this.onlineListener && this.onlineTarget) {
          this.onlineTarget.removeEventListener('online', this.onlineListener);
          this.onlineListener = null;
        }
        this.waitResolve = null;
        resolve(source);
      };

      this.waitResolve = finish;
      this.waitTimer = setTimeout(() => finish('timer'), waitMs);
      if (allowOnlineWake && this.onlineTarget) {
        this.onlineListener = () => finish('online');
        this.onlineTarget.addEventListener('online', this.onlineListener);
      }
    });
  }

  private finishWait(source: 'timer' | 'online') {
    this.waitResolve?.(source);
  }
}

export const createInterviewSttRecoveryCoordinator = (
  options: ConstructorParameters<typeof InterviewSttRecoveryCoordinator>[0] = {},
) => new InterviewSttRecoveryCoordinator(options);

export const isInterviewSttRecoveryCancelled = (error: unknown) => (
  error instanceof InterviewSttRecoveryCancelledError
);
